import { getBudgetExchangeRates } from '@/lib/budgetExchangeRates';
import { apiFetch } from '@/lib/api';

// ===============================
// Type Definitions
// ===============================

/**
 * Image object for destination/activity images
 */
export interface ImageObject {
  url: string;
  caption?: string;
  attachment_id?: string;
  sort_order: number;
}

/**
 * Video object for destination videos
 */
export interface VideoObject {
  url: string;
  attachment_id?: string;
  thumbnail?: string;
}

/**
 * Media layout preference for destination display
 */
export type MediaLayout = 'gallery' | 'grid' | 'video';

/**
 * Annual climate data point
 */
export interface AnnualClimateDataPoint {
  month: string;
  avg_high: number;
  avg_low: number;
  rainfall: number;
}

/**
 * Destination content (global/reusable)
 * Note: Weather and pricing have been moved to proposal-specific tables
 */
export interface DestinationContent {
  id: string;
  created_by_organization_id: string | null;
  city: string;
  country: string;
  region: string | null;
  short_description: string | null;
  long_description: string | null;
  images: ImageObject[];
  video: VideoObject | null;
  media_layout: MediaLayout | null;
  key_pros: string[];
  considerations: string[];
  best_season: string | null;
  last_updated_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Input for creating/updating destination content
 */
export interface UpsertDestinationContentInput {
  city: string;
  country: string;
  region?: string;
  short_description?: string;
  long_description?: string;
  images?: ImageObject[];
  video?: VideoObject | null;
  media_layout?: MediaLayout | null;
  key_pros?: string[];
  considerations?: string[];
  best_season?: string;
}

export interface UpdateDestinationContentInput {
  short_description?: string | null;
  long_description?: string | null;
  images?: ImageObject[];
  video?: VideoObject | null;
  media_layout?: MediaLayout | null;
  key_pros?: string[];
  considerations?: string[];
  best_season?: string | null;
  region?: string | null;
}

// ================================
// Research-Specific Types
// ================================

/**
 * Research destination weather data (proposal-specific)
 */
export interface ResearchDestinationWeather {
  id: string;
  research_destination_id: string;
  day_avg_temperature: number | null;
  night_avg_temperature: number | null;
  rainfall: number | null;
  rainy_days: number | null;
  humidity: number | null;
  weather_description: string | null;
  travel_tip_weather: string | null;
  travel_tip_packing: string | null;
  travel_tip_local_events: string | null;
  annual_climate_data: AnnualClimateDataPoint[] | null;
  travel_month: string | null;
  researched_at: string | null;
  llm_log_id: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Input for upserting research destination weather
 */
export interface UpsertResearchWeatherInput {
  day_avg_temperature?: number | null;
  night_avg_temperature?: number | null;
  rainfall?: number | null;
  rainy_days?: number | null;
  humidity?: number | null;
  weather_description?: string | null;
  travel_tip_weather?: string | null;
  travel_tip_packing?: string | null;
  travel_tip_local_events?: string | null;
  annual_climate_data?: AnnualClimateDataPoint[] | null;
  travel_month?: string | null;
}

/**
 * Research destination activity (proposal-specific copy of global)
 */
export interface ResearchDestinationActivity {
  id: string;
  research_destination_id: string;
  global_activity_id: string | null;
  name: string;
  description: string | null;
  category: ActivityCategory | null;
  intensity: ActivityIntensity | null;
  duration_hours: number | null;
  estimated_price_per_person: number | null;
  currency_code: string;
  images: ImageObject[];
  sort_order: number;
  is_included: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Input for creating a research destination activity
 */
export interface CreateResearchActivityInput {
  global_activity_id?: string | null;
  name: string;
  description?: string | null;
  category?: ActivityCategory | null;
  intensity?: ActivityIntensity | null;
  duration_hours?: number | null;
  estimated_price_per_person?: number | null;
  currency_code?: string;
  images?: ImageObject[];
  sort_order?: number;
  is_included?: boolean;
}

/**
 * Input for updating a research destination activity
 */
export interface UpdateResearchActivityInput {
  name?: string;
  description?: string | null;
  category?: ActivityCategory | null;
  intensity?: ActivityIntensity | null;
  duration_hours?: number | null;
  estimated_price_per_person?: number | null;
  currency_code?: string;
  images?: ImageObject[];
  sort_order?: number;
  is_included?: boolean;
}

/**
 * Research destination event (proposal-specific only, no global template)
 */
export interface ResearchDestinationEvent {
  id: string;
  research_destination_id: string;
  name: string;
  description: string | null;
  tag: EventTag | null;
  estimated_price_per_person: number | null;
  participants: string | null;
  images: ImageObject[];
  sort_order: number;
  is_included: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Input for creating a research destination event
 */
export interface CreateResearchEventInput {
  name: string;
  description?: string | null;
  tag?: EventTag | null;
  estimated_price_per_person?: number | null;
  participants?: string | null;
  images?: ImageObject[];
  sort_order?: number;
  is_included?: boolean;
}

/**
 * Input for updating a research destination event
 */
export interface UpdateResearchEventInput {
  name?: string;
  description?: string | null;
  tag?: EventTag | null;
  estimated_price_per_person?: number | null;
  participants?: string | null;
  images?: ImageObject[];
  sort_order?: number;
  is_included?: boolean;
}

/**
 * Activity categories
 */
export type ActivityCategory = 
  | 'culture'
  | 'adventure'
  | 'food_drink'
  | 'wellness'
  | 'entertainment'
  | 'nature'
  | 'team_building'
  | 'sightseeing'
  | 'water_sports'
  | 'other';

/**
 * Activity intensity levels
 */
export type ActivityIntensity = 'relaxed' | 'moderate' | 'active';

/**
 * Destination activity
 */
export interface DestinationActivity {
  id: string;
  created_by_organization_id: string | null;
  city: string;
  country: string;
  name: string;
  description: string | null;
  category: ActivityCategory | null;
  intensity: ActivityIntensity | null;
  duration_hours: number | null;
  estimated_price_per_person: number | null;
  currency_code: string;
  images: ImageObject[];
  sort_order: number;
  is_active: boolean;
  last_updated_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Input for creating an activity
 */
export interface CreateActivityInput {
  city: string;
  country: string;
  name: string;
  description?: string;
  category?: ActivityCategory;
  intensity?: ActivityIntensity;
  duration_hours?: number;
  estimated_price_per_person?: number;
  currency_code?: string;
  images?: ImageObject[];
  sort_order?: number;
}

/**
 * Input for updating an activity
 */
export interface UpdateActivityInput {
  name?: string;
  description?: string | null;
  category?: ActivityCategory | null;
  intensity?: ActivityIntensity | null;
  duration_hours?: number | null;
  estimated_price_per_person?: number | null;
  currency_code?: string;
  images?: ImageObject[];
  sort_order?: number;
  is_active?: boolean;
}

// ================================
// Destination Events Types
// ================================

/**
 * Event tags
 */
export type EventTag = 'off_venue' | 'welcome_reception' | 'final_dinner' | 'nightlife';

/**
 * Destination event
 */
export interface DestinationEvent {
  id: string;
  created_by_organization_id: string | null;
  city: string;
  country: string;
  name: string;
  description: string | null;
  tag: EventTag | null;
  estimated_price_per_person: number | null;
  participants: string | null;
  images: ImageObject[];
  sort_order: number;
  is_active: boolean;
  last_updated_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Input for creating an event
 */
export interface CreateEventInput {
  city: string;
  country: string;
  name: string;
  description?: string;
  tag?: EventTag;
  estimated_price_per_person?: number;
  participants?: string;
  images?: ImageObject[];
  sort_order?: number;
}

/**
 * Input for updating an event
 */
export interface UpdateEventInput {
  name?: string;
  description?: string | null;
  tag?: EventTag | null;
  estimated_price_per_person?: number | null;
  participants?: string | null;
  images?: ImageObject[];
  sort_order?: number;
  is_active?: boolean;
}

// ================================
// Event Library Types (per-organization)
// ================================

export interface EventLibraryItem {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  tag: EventTag | null;
  estimated_price_per_person: number | null;
  participants: string | null;
  images: ImageObject[];
  sort_order: number;
  is_active: boolean;
  created_by_user_id: string | null;
  last_updated_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateEventLibraryInput {
  name: string;
  description?: string;
  tag?: EventTag;
  estimated_price_per_person?: number;
  participants?: string;
  images?: ImageObject[];
  sort_order?: number;
}

export interface UpdateEventLibraryInput {
  name?: string;
  description?: string | null;
  tag?: EventTag | null;
  estimated_price_per_person?: number | null;
  participants?: string | null;
  images?: ImageObject[];
  sort_order?: number;
  is_active?: boolean;
}

// ================================
// Activity Library Types (per-organization)
// ================================

export interface ActivityLibraryItem {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  category: ActivityCategory | null;
  intensity: ActivityIntensity | null;
  duration_hours: number | null;
  estimated_price_per_person: number | null;
  currency_code: string;
  images: ImageObject[];
  sort_order: number;
  is_active: boolean;
  created_by_user_id: string | null;
  last_updated_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateActivityLibraryInput {
  name: string;
  description?: string;
  category?: ActivityCategory;
  intensity?: ActivityIntensity;
  duration_hours?: number;
  estimated_price_per_person?: number;
  currency_code?: string;
  images?: ImageObject[];
  sort_order?: number;
}

export interface UpdateActivityLibraryInput {
  name?: string;
  description?: string | null;
  category?: ActivityCategory | null;
  intensity?: ActivityIntensity | null;
  duration_hours?: number | null;
  estimated_price_per_person?: number | null;
  currency_code?: string;
  images?: ImageObject[];
  sort_order?: number;
  is_active?: boolean;
}

/**
 * Share link types
 */
export type ShareLinkType = 'proposal' | 'research';

/**
 * Proposal share link
 */
export type OverviewLayoutType = 'masonry' | 'two-column' | 'multi-column';

export interface ProposalShareLink {
  id: string;
  organization_id: string;
  event_id: string;
  token: string;
  link_type: ShareLinkType;
  whitelisted_email_domains: string[];
  is_active: boolean;
  is_public: boolean;
  expires_at: string | null;
  overview_layout: OverviewLayoutType | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

/**
 * Client engagement for an event's share links (CRHQ 16687dd9).
 * Client views only; the team's own previews are tagged separately.
 */
export interface ProposalAnalytics {
  links: Array<Pick<ProposalShareLink, 'id' | 'token' | 'link_type' | 'is_active'> & {
    first_viewed_at: string | null;
    last_viewed_at: string | null;
    view_count: number;
  }>;
  visits: Array<{
    link_id: string;
    session_key: string;
    actor_type: 'client' | 'platform';
    email: string | null;
    first_seen: string;
    last_seen: string;
    dwell_seconds: number;
    sections: string[];
    venue_ids: string[];
  } & VisitLocation>;
  totals: {
    visit_count: number;
    client_visit_count: number;
    section_views: number;
    hotel_views: number;
    dwell_seconds: number;
    unique_viewers: number;
  };
  sections: Array<{ section: string; visit_count: number }>;
  hotels: Array<{ venue_id: string; visit_count: number }>;
  venueNames: Array<{ id: string; name: string }>;
  shortlist: Array<{ venue_id: string; destination_id: string; created_at: string }>;
  feedback: Array<{ name: string; comments: string; created_at: string }>;
  emails: Array<{ email: string; first_seen: string; last_seen: string }>;
  /** Client visits per approximate location, most visits first ('ZZ' unknown last) */
  locations?: Array<VisitLocation & { visit_count: number }>;
}

/**
 * Approximate visitor location, derived server-side from the IP at visit time
 * (the IP itself is never stored). country_code: ISO alpha-2, 'ZZ' = unknown,
 * null = visit recorded before location tracking. region_name: US/CA only.
 */
export interface VisitLocation {
  country_code: string | null;
  country_name: string | null;
  region_name: string | null;
}

/**
 * Input for creating a share link
 */
export interface CreateShareLinkInput {
  link_type: ShareLinkType;
  whitelisted_email_domains?: string[];
  is_public?: boolean;
  expires_at?: string | null;
  overview_layout?: OverviewLayoutType | null;
}

/**
 * Input for updating a share link
 */
export interface UpdateShareLinkInput {
  whitelisted_email_domains?: string[];
  is_active?: boolean;
  is_public?: boolean;
  expires_at?: string | null;
  overview_layout?: OverviewLayoutType | null;
}

/**
 * Share link creation response
 */
export interface CreateShareLinkResponse {
  link: ProposalShareLink;
  url: string;
}

/**
 * Public share link info (minimal, for login page)
 */
export interface PublicShareLinkInfo {
  link_type: ShareLinkType;
  event_name: string;
  client_name: string;
  event_id?: string; // Optional - may be included by backend
  overview_layout?: OverviewLayoutType | null; // Optional - layout preference for overview page
  organization_id?: string; // Optional - organization ID for fetching settings
  is_public?: boolean; // If true, anyone with link can access without auth
}

/**
 * OTP request response
 */
export interface OtpRequestResponse {
  session_id: string;
  masked_email: string;
  expires_in_minutes: number;
}

/**
 * OTP verification response
 */
export interface OtpVerifyResponse {
  session_id: string;
  email: string;
  is_verified: boolean;
  session_expires_at: string;
}

/**
 * Session check response
 */
export interface SessionCheckResponse {
  is_valid: boolean;
  email?: string;
  session_expires_at?: string;
}

// ===============================
// Helper function for API responses
// ===============================

async function parseResponse<T>(res: Response, errorMsg: string): Promise<T> {
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const error = new Error(errorData.error?.message || errorData.message || errorMsg);
    (error as any).status = res.status;
    (error as any).response = errorData;
    throw error;
  }
  const json = await res.json();
  return json.data ?? json;
}

// ===============================
// Service Class
// ===============================

/**
 * Frontend service for proposal management API
 */
export class ProposalService {
  // ===============================
  // Destination Content
  // ===============================

  /**
   * Get destination content by city/country
   */
  async getDestinationContent(city: string, country: string): Promise<DestinationContent | null> {
    const res = await apiFetch(
      `/api/destinations/content/${encodeURIComponent(city)}/${encodeURIComponent(country)}`
    );
    if (res.status === 404) return null;
    const data = await parseResponse<{ content: DestinationContent }>(res, 'Failed to get destination content');
    return data.content;
  }

  /**
   * List all destination content
   */
  async listDestinationContent(): Promise<DestinationContent[]> {
    const res = await apiFetch('/api/destinations/content');
    const data = await parseResponse<{ items: DestinationContent[] }>(res, 'Failed to list destination content');
    return data.items;
  }

  /**
   * Create or update destination content
   */
  async upsertDestinationContent(input: UpsertDestinationContentInput): Promise<DestinationContent> {
    const res = await apiFetch('/api/destinations/content', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const data = await parseResponse<{ content: DestinationContent }>(res, 'Failed to save destination content');
    return data.content;
  }

  /**
   * Update destination content by ID
   */
  async updateDestinationContent(id: string, input: UpdateDestinationContentInput): Promise<DestinationContent> {
    const res = await apiFetch(`/api/destinations/content/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    const data = await parseResponse<{ content: DestinationContent }>(res, 'Failed to update destination content');
    return data.content;
  }

  /**
   * Pre-flight: how many activities and events would be cascade-deleted
   * if we deleted this destination content right now.
   */
  async previewDestinationContentDelete(id: string): Promise<{
    city: string;
    country: string;
    activities: number;
    events: number;
  }> {
    const res = await apiFetch(`/api/destinations/content/${id}/cascade-preview`);
    return parseResponse<{
      city: string;
      country: string;
      activities: number;
      events: number;
    }>(res, 'Failed to preview destination delete');
  }

  /**
   * Hard-delete a destination content row plus its activities and events
   * (matched by city+country). Existing proposals are unaffected — they
   * read from per-event snapshot tables, not from these globals.
   */
  async deleteDestinationContent(id: string): Promise<{
    deletedActivities: number;
    deletedEvents: number;
  }> {
    const res = await apiFetch(`/api/destinations/content/${id}`, { method: 'DELETE' });
    return parseResponse<{
      deletedActivities: number;
      deletedEvents: number;
    }>(res, 'Failed to delete destination content');
  }

  /**
   * Research weather data using AI for a research destination
   * @param researchDestinationId - Research destination ID (proposal-specific)
   * @param city - City name
   * @param country - Country name
   * @param travelMonth - Travel month(s) e.g., "May" or "May-June"
   */
  async researchWeather(
    researchDestinationId: string,
    city: string,
    country: string,
    travelMonth: string
  ): Promise<{
    weather: ResearchDestinationWeather;
    annual_data_pending: boolean;
    llm_log_id: string;
    llm_model: string;
    llm_cost: string;
  }> {
    const res = await apiFetch(`/api/research-destinations/${researchDestinationId}/weather/research`, {
      method: 'POST',
      body: JSON.stringify({ city, country, travelMonth }),
    });
    const data = await parseResponse<{
      weather: ResearchDestinationWeather;
      annual_data_pending: boolean;
      llm_log_id: string;
      llm_model: string;
      llm_cost: string;
    }>(res, 'Failed to research weather data');
    return data;
  }

  /**
   * Get weather data for a research destination (requires auth)
   */
  async getResearchWeather(researchDestinationId: string): Promise<ResearchDestinationWeather | null> {
    const res = await apiFetch(`/api/research-destinations/${researchDestinationId}/weather`);
    if (res.status === 404) return null;
    const data = await parseResponse<{ weather: ResearchDestinationWeather }>(res, 'Failed to get weather data');
    return data.weather;
  }

  /**
   * Get weather data for a research destination via public proposal link (no auth required)
   * Use when viewing a proposal as a public link
   */
  async getResearchWeatherForProposal(
    shareLinkToken: string,
    researchDestinationId: string
  ): Promise<ResearchDestinationWeather | null> {
    const res = await apiFetch(
      `/api/public/proposal/${encodeURIComponent(shareLinkToken)}/destinations/${encodeURIComponent(researchDestinationId)}/weather`
    );
    if (res.status === 404) return null;
    const data = await parseResponse<{ weather: ResearchDestinationWeather }>(res, 'Failed to get weather data');
    return data.weather;
  }

  /**
   * Get destination content via public proposal link (no auth required)
   * Use when viewing a proposal as a public link
   */
  async getDestinationContentForProposal(
    shareLinkToken: string,
    city: string,
    country: string
  ): Promise<DestinationContent | null> {
    const res = await apiFetch(
      `/api/public/proposal/${encodeURIComponent(shareLinkToken)}/destinations/content/${encodeURIComponent(city)}/${encodeURIComponent(country)}`,
      { skipRedirect: true, credentials: 'include' }
    );
    if (res.status === 404) return null;
    const data = await parseResponse<{ content: DestinationContent }>(res, 'Failed to get destination content');
    return data.content;
  }

  /**
   * List activities for a destination via public proposal link (no auth required)
   * Use when viewing a proposal as a public link
   */
  async listActivitiesForProposal(
    shareLinkToken: string,
    city: string,
    country: string
  ): Promise<DestinationActivity[]> {
    const res = await apiFetch(
      `/api/public/proposal/${encodeURIComponent(shareLinkToken)}/destinations/activities/${encodeURIComponent(city)}/${encodeURIComponent(country)}`,
      { skipRedirect: true, credentials: 'include' }
    );
    const data = await parseResponse<{ items: DestinationActivity[] }>(res, 'Failed to list activities');
    return data.items;
  }

  /**
   * List events for a destination via public proposal link (no auth required)
   * Use when viewing a proposal as a public link
   */
  async listEventsForProposal(
    shareLinkToken: string,
    city: string,
    country: string
  ): Promise<DestinationEvent[]> {
    const res = await apiFetch(
      `/api/public/proposal/${encodeURIComponent(shareLinkToken)}/destinations/events/${encodeURIComponent(city)}/${encodeURIComponent(country)}`,
      { skipRedirect: true, credentials: 'include' }
    );
    const data = await parseResponse<{ items: DestinationEvent[] }>(res, 'Failed to list events');
    return data.items;
  }

  /**
   * List research activities by destination ID via public proposal link (no auth required)
   * Same approach as the research-proposal page — uses destination ID, handles regions
   */
  async listResearchActivitiesForProposal(
    shareLinkToken: string,
    researchDestinationId: string
  ): Promise<DestinationActivity[]> {
    const res = await apiFetch(
      `/api/public/proposal/${encodeURIComponent(shareLinkToken)}/destinations/${encodeURIComponent(researchDestinationId)}/activities`,
      { skipRedirect: true, credentials: 'include' }
    );
    if (res.status === 404) return [];
    const data = await parseResponse<{ items: DestinationActivity[] }>(res, 'Failed to list research activities');
    return data.items;
  }

  /**
   * List research events by destination ID via public proposal link (no auth required)
   * Same approach as the research-proposal page — uses destination ID, handles regions
   */
  async listResearchEventsForProposal(
    shareLinkToken: string,
    researchDestinationId: string
  ): Promise<DestinationEvent[]> {
    const res = await apiFetch(
      `/api/public/proposal/${encodeURIComponent(shareLinkToken)}/destinations/${encodeURIComponent(researchDestinationId)}/events`,
      { skipRedirect: true, credentials: 'include' }
    );
    if (res.status === 404) return [];
    const data = await parseResponse<{ items: DestinationEvent[] }>(res, 'Failed to list research events');
    return data.items;
  }

  /**
   * Update weather data for a research destination
   */
  async updateResearchWeather(
    researchDestinationId: string,
    input: UpsertResearchWeatherInput
  ): Promise<ResearchDestinationWeather> {
    const res = await apiFetch(`/api/research-destinations/${researchDestinationId}/weather`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    const data = await parseResponse<{ weather: ResearchDestinationWeather }>(res, 'Failed to update weather data');
    return data.weather;
  }

  // ===============================
  // Research Destination Activities
  // ===============================

  /**
   * List activities for a research destination
   */
  async listResearchActivities(
    researchDestinationId: string,
    includeExcluded = false
  ): Promise<ResearchDestinationActivity[]> {
    const params = includeExcluded ? '?include_excluded=true' : '';
    const res = await apiFetch(`/api/research-destinations/${researchDestinationId}/activities${params}`);
    const data = await parseResponse<{ items: ResearchDestinationActivity[] }>(res, 'Failed to list research activities');
    return data.items;
  }

  /**
   * Create a research destination activity
   */
  async createResearchActivity(
    researchDestinationId: string,
    input: CreateResearchActivityInput
  ): Promise<ResearchDestinationActivity> {
    const res = await apiFetch(`/api/research-destinations/${researchDestinationId}/activities`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const data = await parseResponse<{ activity: ResearchDestinationActivity }>(res, 'Failed to create research activity');
    return data.activity;
  }

  /**
   * Update a research destination activity
   */
  async updateResearchActivity(
    researchDestinationId: string,
    id: string,
    input: UpdateResearchActivityInput
  ): Promise<ResearchDestinationActivity> {
    const res = await apiFetch(`/api/research-destinations/${researchDestinationId}/activities/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    const data = await parseResponse<{ activity: ResearchDestinationActivity }>(res, 'Failed to update research activity');
    return data.activity;
  }

  /**
   * Delete a research destination activity
   */
  async deleteResearchActivity(researchDestinationId: string, id: string): Promise<void> {
    const res = await apiFetch(`/api/research-destinations/${researchDestinationId}/activities/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to delete research activity');
    }
  }

  /**
   * Copy global activities to a research destination
   */
  async copyGlobalActivitiesToResearch(
    researchDestinationId: string,
    globalActivityIds: string[]
  ): Promise<{ created_ids: string[] }> {
    const res = await apiFetch(`/api/research-destinations/${researchDestinationId}/activities/copy-from-global`, {
      method: 'POST',
      body: JSON.stringify({ global_activity_ids: globalActivityIds }),
    });
    const data = await parseResponse<{ created_ids: string[] }>(res, 'Failed to copy activities');
    return data;
  }

  /**
   * Promote a research activity to global
   */
  async promoteActivityToGlobal(
    researchDestinationId: string,
    researchActivityId: string,
    city: string,
    country: string
  ): Promise<{ global_activity_id: string }> {
    const res = await apiFetch(`/api/research-destinations/${researchDestinationId}/activities/${researchActivityId}/promote-to-global`, {
      method: 'POST',
      body: JSON.stringify({ city, country }),
    });
    const data = await parseResponse<{ global_activity_id: string }>(res, 'Failed to promote activity to global');
    return data;
  }

  // ===============================
  // Research Destination Events
  // ===============================

  /**
   * List events for a research destination
   */
  async listResearchEvents(
    researchDestinationId: string,
    includeExcluded = false
  ): Promise<ResearchDestinationEvent[]> {
    const params = includeExcluded ? '?include_excluded=true' : '';
    const res = await apiFetch(`/api/research-destinations/${researchDestinationId}/events${params}`);
    const data = await parseResponse<{ items: ResearchDestinationEvent[] }>(res, 'Failed to list research events');
    return data.items;
  }

  /**
   * Create a research destination event
   */
  async createResearchEvent(
    researchDestinationId: string,
    input: CreateResearchEventInput
  ): Promise<ResearchDestinationEvent> {
    const res = await apiFetch(`/api/research-destinations/${researchDestinationId}/events`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const data = await parseResponse<{ event: ResearchDestinationEvent }>(res, 'Failed to create research event');
    return data.event;
  }

  /**
   * Update a research destination event
   */
  async updateResearchEvent(id: string, input: UpdateResearchEventInput): Promise<ResearchDestinationEvent> {
    const res = await apiFetch(`/api/research-destinations/events/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    const data = await parseResponse<{ event: ResearchDestinationEvent }>(res, 'Failed to update research event');
    return data.event;
  }

  /**
   * Delete a research destination event
   */
  async deleteResearchEvent(id: string): Promise<void> {
    const res = await apiFetch(`/api/research-destinations/events/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to delete research event');
    }
  }

  /**
   * Call AI to research destination content (description, pros, considerations).
   */
  async researchDestinationContent(city: string, country: string): Promise<{
    content: {
      short_description: string;
      key_pros: string[];
      considerations: string[];
    };
    llm_log_id: string;
    llm_model: string;
    llm_cost: string;
  }> {
    const res = await apiFetch('/api/destinations/content/research', {
      method: 'POST',
      body: JSON.stringify({ city, country }),
    });
    const data = await parseResponse<{
      content: {
        short_description: string;
        key_pros: string[];
        considerations: string[];
      };
      llm_log_id: string;
      llm_model: string;
      llm_cost: string;
    }>(res, 'Failed to research destination content');
    return data;
  }

  // ===============================
  // Destination Activities
  // ===============================

  /**
   * List activities for a destination
   */
  async listActivities(city: string, country: string, includeInactive = false): Promise<DestinationActivity[]> {
    const params = includeInactive ? '?include_inactive=true' : '';
    const res = await apiFetch(
      `/api/destinations/${encodeURIComponent(city)}/${encodeURIComponent(country)}/activities${params}`
    );
    const data = await parseResponse<{ items: DestinationActivity[] }>(res, 'Failed to list activities');
    return data.items;
  }

  /**
   * Create an activity
   */
  async createActivity(input: CreateActivityInput): Promise<DestinationActivity> {
    const res = await apiFetch('/api/destinations/activities', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const data = await parseResponse<{ activity: DestinationActivity }>(res, 'Failed to create activity');
    return data.activity;
  }

  /**
   * Update an activity
   */
  async updateActivity(id: string, input: UpdateActivityInput): Promise<DestinationActivity> {
    const res = await apiFetch(`/api/destinations/activities/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    const data = await parseResponse<{ activity: DestinationActivity }>(res, 'Failed to update activity');
    return data.activity;
  }

  /**
   * Delete an activity.
   * Default: soft delete (sets is_active = false).
   * Pass { hard: true } to permanently remove the row.
   */
  async deleteActivity(id: string, options: { hard?: boolean } = {}): Promise<void> {
    const qs = options.hard ? '?hard=true' : '';
    const res = await apiFetch(`/api/destinations/activities/${id}${qs}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to delete activity');
    }
  }

  // ===============================
  // Destination Events
  // ===============================

  /**
   * List events for a destination
   */
  async listEvents(city: string, country: string, includeInactive = false): Promise<DestinationEvent[]> {
    const params = includeInactive ? '?include_inactive=true' : '';
    const res = await apiFetch(
      `/api/destinations/${encodeURIComponent(city)}/${encodeURIComponent(country)}/events${params}`
    );
    const data = await parseResponse<{ items: DestinationEvent[] }>(res, 'Failed to list events');
    return data.items;
  }

  /**
   * Create an event
   */
  async createEvent(input: CreateEventInput): Promise<DestinationEvent> {
    const res = await apiFetch('/api/destinations/events', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const data = await parseResponse<{ event: DestinationEvent }>(res, 'Failed to create event');
    return data.event;
  }

  /**
   * Update an event
   */
  async updateEvent(id: string, input: UpdateEventInput): Promise<DestinationEvent> {
    const res = await apiFetch(`/api/destinations/events/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    const data = await parseResponse<{ event: DestinationEvent }>(res, 'Failed to update event');
    return data.event;
  }

  /**
   * Delete an event.
   * Default: soft delete (sets is_active = false).
   * Pass { hard: true } to permanently remove the row.
   */
  async deleteEvent(id: string, options: { hard?: boolean } = {}): Promise<void> {
    const qs = options.hard ? '?hard=true' : '';
    const res = await apiFetch(`/api/destinations/events/${id}${qs}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to delete event');
    }
  }

  // ===============================
  // Event Library (per-organization)
  // ===============================

  async listEventLibrary(includeInactive = false): Promise<EventLibraryItem[]> {
    const params = includeInactive ? '?include_inactive=true' : '';
    const res = await apiFetch(`/api/event-library${params}`);
    const data = await parseResponse<{ items: EventLibraryItem[] }>(res, 'Failed to list event library');
    return data.items;
  }

  async getEventLibraryItem(id: string): Promise<EventLibraryItem> {
    const res = await apiFetch(`/api/event-library/${id}`);
    const data = await parseResponse<{ event: EventLibraryItem }>(res, 'Failed to get library event');
    return data.event;
  }

  async createEventLibraryItem(input: CreateEventLibraryInput): Promise<EventLibraryItem> {
    const res = await apiFetch('/api/event-library', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const data = await parseResponse<{ event: EventLibraryItem }>(res, 'Failed to create library event');
    return data.event;
  }

  async updateEventLibraryItem(id: string, input: UpdateEventLibraryInput): Promise<EventLibraryItem> {
    const res = await apiFetch(`/api/event-library/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    const data = await parseResponse<{ event: EventLibraryItem }>(res, 'Failed to update library event');
    return data.event;
  }

  async deleteEventLibraryItem(id: string): Promise<void> {
    const res = await apiFetch(`/api/event-library/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to delete library event');
    }
  }

  async copyLibraryEventToDestination(
    libraryEventId: string,
    city: string,
    country: string
  ): Promise<DestinationEvent> {
    const res = await apiFetch('/api/event-library/copy-to-destination', {
      method: 'POST',
      body: JSON.stringify({ library_event_id: libraryEventId, city, country }),
    });
    const data = await parseResponse<{ event: DestinationEvent }>(res, 'Failed to copy library event');
    return data.event;
  }

  async promoteEventToLibrary(eventId: string): Promise<EventLibraryItem> {
    const res = await apiFetch('/api/event-library/promote', {
      method: 'POST',
      body: JSON.stringify({ event_id: eventId }),
    });
    const data = await parseResponse<{ event: EventLibraryItem }>(res, 'Failed to promote event');
    return data.event;
  }

  // ===============================
  // Activity Library (per-organization)
  // ===============================

  async listActivityLibrary(includeInactive = false): Promise<ActivityLibraryItem[]> {
    const params = includeInactive ? '?include_inactive=true' : '';
    const res = await apiFetch(`/api/activity-library${params}`);
    const data = await parseResponse<{ items: ActivityLibraryItem[] }>(res, 'Failed to list activity library');
    return data.items;
  }

  async getActivityLibraryItem(id: string): Promise<ActivityLibraryItem> {
    const res = await apiFetch(`/api/activity-library/${id}`);
    const data = await parseResponse<{ activity: ActivityLibraryItem }>(res, 'Failed to get library activity');
    return data.activity;
  }

  async createActivityLibraryItem(input: CreateActivityLibraryInput): Promise<ActivityLibraryItem> {
    const res = await apiFetch('/api/activity-library', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const data = await parseResponse<{ activity: ActivityLibraryItem }>(res, 'Failed to create library activity');
    return data.activity;
  }

  async updateActivityLibraryItem(id: string, input: UpdateActivityLibraryInput): Promise<ActivityLibraryItem> {
    const res = await apiFetch(`/api/activity-library/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    const data = await parseResponse<{ activity: ActivityLibraryItem }>(res, 'Failed to update library activity');
    return data.activity;
  }

  async deleteActivityLibraryItem(id: string): Promise<void> {
    const res = await apiFetch(`/api/activity-library/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to delete library activity');
    }
  }

  async copyLibraryActivityToDestination(
    libraryActivityId: string,
    city: string,
    country: string
  ): Promise<DestinationActivity> {
    const res = await apiFetch('/api/activity-library/copy-to-destination', {
      method: 'POST',
      body: JSON.stringify({ library_activity_id: libraryActivityId, city, country }),
    });
    const data = await parseResponse<{ activity: DestinationActivity }>(res, 'Failed to copy library activity');
    return data.activity;
  }

  /**
   * Copy a library activity directly into a research destination.
   * Used inside the proposal wizard so added activities appear in that
   * proposal's destination list without polluting the destination pool.
   */
  async copyLibraryActivityToResearch(
    libraryActivityId: string,
    researchDestinationId: string
  ): Promise<string> {
    const res = await apiFetch('/api/activity-library/copy-to-research', {
      method: 'POST',
      body: JSON.stringify({
        library_activity_id: libraryActivityId,
        research_destination_id: researchDestinationId,
      }),
    });
    const data = await parseResponse<{ research_activity_id: string }>(
      res,
      'Failed to copy library activity to research destination'
    );
    return data.research_activity_id;
  }

  async promoteActivityToLibrary(activityId: string): Promise<ActivityLibraryItem> {
    const res = await apiFetch('/api/activity-library/promote', {
      method: 'POST',
      body: JSON.stringify({ activity_id: activityId }),
    });
    const data = await parseResponse<{ activity: ActivityLibraryItem }>(res, 'Failed to promote activity');
    return data.activity;
  }

  async promoteResearchActivityToLibrary(researchActivityId: string): Promise<ActivityLibraryItem> {
    const res = await apiFetch('/api/activity-library/promote-research', {
      method: 'POST',
      body: JSON.stringify({ research_activity_id: researchActivityId }),
    });
    const data = await parseResponse<{ activity: ActivityLibraryItem }>(res, 'Failed to save activity to library');
    return data.activity;
  }

  // ===============================
  // Share Links
  // ===============================

  /**
   * List share links for an event
   */
  async listShareLinks(eventId: string): Promise<ProposalShareLink[]> {
    const res = await apiFetch(`/api/events/${eventId}/share-links`);
    const data = await parseResponse<{ items: ProposalShareLink[] }>(res, 'Failed to list share links');
    return data.items;
  }

  /**
   * Get combined whitelist for an event (merged from all active links)
   */
  async getEventWhitelist(eventId: string): Promise<string[]> {
    const res = await apiFetch(`/api/events/${eventId}/share-links/whitelist`);
    const data = await parseResponse<{ whitelist: string[] }>(res, 'Failed to get whitelist');
    return data.whitelist;
  }

  /**
   * Create a share link
   */
  async createShareLink(eventId: string, input: CreateShareLinkInput): Promise<CreateShareLinkResponse> {
    const res = await apiFetch(`/api/events/${eventId}/share-links`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const data = await parseResponse<CreateShareLinkResponse>(res, 'Failed to create share link');
    return data;
  }

  /**
   * Update a share link
   */
  async updateShareLink(eventId: string, linkId: string, input: UpdateShareLinkInput): Promise<ProposalShareLink> {
    const res = await apiFetch(`/api/events/${eventId}/share-links/${linkId}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    const data = await parseResponse<{ link: ProposalShareLink }>(res, 'Failed to update share link');
    return data.link;
  }

  /**
   * Delete a share link
   */
  async deleteShareLink(eventId: string, linkId: string): Promise<void> {
    const res = await apiFetch(`/api/events/${eventId}/share-links/${linkId}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to delete share link');
    }
  }

  /**
   * Proposal link analytics — has the client opened the link, when, how
   * often, and what they looked at. null = link not created yet.
   */
  async getProposalAnalytics(eventId: string, linkType: 'proposal' | 'research' = 'proposal'): Promise<ProposalAnalytics | null> {
    const res = await apiFetch(`/api/events/${eventId}/proposal-analytics?link_type=${linkType}`);
    const data = await parseResponse<ProposalAnalytics | null>(res, 'Failed to load proposal analytics');
    return data;
  }

  // ===============================
  // Public Access (No Auth)
  // ===============================

  /**
   * Get public share link info
   */
  async getPublicLinkInfo(token: string): Promise<PublicShareLinkInfo> {
    const res = await apiFetch(`/api/public/share/${token}/info`, { skipRedirect: true });
    const data = await parseResponse<PublicShareLinkInfo>(res, 'Failed to get link info');
    return data;
  }

  /**
   * Request OTP for access
   */
  async requestOtp(token: string, email: string): Promise<OtpRequestResponse> {
    const res = await apiFetch(`/api/public/share/${token}/request-access`, {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipRedirect: true,
    });
    const data = await parseResponse<OtpRequestResponse>(res, 'Failed to request access code');
    return data;
  }

  /**
   * Verify OTP
   */
  async verifyOtp(token: string, sessionId: string, otp: string): Promise<OtpVerifyResponse> {
    const res = await apiFetch(`/api/public/share/${token}/verify`, {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId, otp }),
      skipRedirect: true,
    });
    const data = await parseResponse<OtpVerifyResponse>(res, 'Failed to verify access code');
    return data;
  }

  /**
   * Check session validity
   */
  async checkSession(token: string, sessionId: string): Promise<SessionCheckResponse> {
    const res = await apiFetch(
      `/api/public/share/${token}/session?session_id=${sessionId}`,
      { skipRedirect: true }
    );
    const data = await parseResponse<SessionCheckResponse>(res, 'Failed to check session');
    return data;
  }

  /**
   * Check if current user is authenticated and has access to the share link's org
   * This allows platform users to bypass OTP verification
   */
  async checkAuthAccess(token: string): Promise<{ is_authenticated: boolean; has_access: boolean; email?: string }> {
    try {
      const res = await apiFetch(`/api/public/share/${token}/check-auth`, { 
        skipRedirect: true,
        // Include credentials to send auth cookies
        credentials: 'include',
      });
      const data = await parseResponse<{ is_authenticated: boolean; has_access: boolean; email?: string }>(
        res, 
        'Failed to check auth access'
      );
      return data;
    } catch {
      // If the check fails, assume not authenticated
      return { is_authenticated: false, has_access: false };
    }
  }

  /**
   * Get research data (requires valid session OR authenticated platform user)
   * @param sessionId - OTP session ID (optional for authenticated platform users)
   */
  async getResearchData(token: string, sessionId?: string): Promise<any> {
    const headers: Record<string, string> = {};
    if (sessionId) {
      headers['X-Session-Id'] = sessionId;
    }
    
    console.log('[ProposalService.getResearchData] Making request', {
      token,
      hasSessionId: !!sessionId,
      url: `/api/public/research/${token}/data`
    });
    
    const res = await apiFetch(`/api/public/research/${token}/data`, {
      headers,
      skipRedirect: true, // Never redirect - this is a public share link
      skipRefresh: true, // Never try to refresh token - this is a public endpoint
      credentials: 'include', // Include auth cookies (for platform users)
    });
    
    console.log('[ProposalService.getResearchData] Response status:', res.status);
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      console.error('[ProposalService.getResearchData] Error response:', {
        status: res.status,
        error: errorData.error,
        message: errorData.message,
        fullResponse: errorData
      });
      const error = new Error(errorData.error?.message || errorData.message || 'Failed to get research data');
      (error as any).status = res.status;
      (error as any).response = errorData;
      throw error;
    }
    
    const data = await parseResponse<any>(res, 'Failed to get research data');
    return data;
  }

  /**
   * Get proposal data (requires valid session OR authenticated platform user)
   * @param sessionId - OTP session ID (optional for authenticated platform users)
   */
  async getProposalData(token: string, sessionId?: string): Promise<any> {
    const headers: Record<string, string> = {};
    if (sessionId) {
      headers['X-Session-Id'] = sessionId;
    }
    
    const res = await apiFetch(`/api/public/proposal/${token}/data`, {
      headers,
      skipRedirect: true,
      credentials: 'include', // Include auth cookies
    });
    const data = await parseResponse<any>(res, 'Failed to get proposal data');
    return data;
  }

  /**
   * Get client shortlist (hotels selected by client)
   * @param token - Share link token
   * @param sessionId - OTP session ID (optional for authenticated platform users)
   */
  async getClientShortlist(token: string, sessionId?: string): Promise<Array<{
    hotel_id: string;
    destination_id: string;
    destination_city: string;
    destination_country: string;
    hotel_name: string;
    hotel_image: string;
    estimated_per_person?: number;
  }>> {
    const headers: Record<string, string> = {};
    if (sessionId) {
      headers['X-Session-Id'] = sessionId;
    }
    
    const res = await apiFetch(`/api/public/proposal/${token}/client-shortlist`, {
      headers,
      skipRedirect: true,
      credentials: 'include',
    });
    const data = await parseResponse<Array<{
      hotel_id: string;
      destination_id: string;
      destination_city: string;
      destination_country: string;
      hotel_name: string;
      hotel_image: string;
      estimated_per_person?: number;
    }>>(res, 'Failed to get client shortlist');
    return data;
  }

  /**
   * Add hotel to client shortlist
   * @param token - Share link token
   * @param hotelId - Hotel ID
   * @param destinationId - Destination ID
   * @param sessionId - OTP session ID (optional for authenticated platform users)
   */
  async addToClientShortlist(
    token: string,
    hotelId: string,
    destinationId: string,
    sessionId?: string
  ): Promise<void> {
    const headers: Record<string, string> = {};
    if (sessionId) {
      headers['X-Session-Id'] = sessionId;
    }
    
    const res = await apiFetch(`/api/public/proposal/${token}/client-shortlist`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ 
        hotel_id: hotelId, 
        destination_id: destinationId 
      }),
      skipRedirect: true,
      credentials: 'include',
    });
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to add to shortlist');
    }
  }

  /**
   * Remove hotel from client shortlist
   * @param token - Share link token
   * @param hotelId - Hotel ID
   * @param sessionId - OTP session ID (optional for authenticated platform users)
   */
  async removeFromClientShortlist(
    token: string,
    hotelId: string,
    sessionId?: string
  ): Promise<void> {
    const headers: Record<string, string> = {};
    if (sessionId) {
      headers['X-Session-Id'] = sessionId;
    }
    
    const res = await apiFetch(`/api/public/proposal/${token}/client-shortlist/${hotelId}`, {
      method: 'DELETE',
      headers,
      skipRedirect: true,
      credentials: 'include',
    });
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to remove from shortlist');
    }
  }

  /**
   * Send feedback
   * @param token - Share link token
   * @param name - Name of person giving feedback
   * @param comments - Feedback comments
   * @param sessionId - OTP session ID (optional for authenticated platform users)
   */
  async sendFeedback(
    token: string,
    name: string,
    comments: string,
    sessionId?: string
  ): Promise<void> {
    const headers: Record<string, string> = {};
    if (sessionId) {
      headers['X-Session-Id'] = sessionId;
    }
    
    const res = await apiFetch(`/api/public/proposal/${token}/feedback`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ name, comments }),
      skipRedirect: true,
      credentials: 'include',
    });
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to send feedback');
    }
  }
  /**
   * Get venue budget breakdown (public share link)
   * @param token - Share link token
   * @param shortlistId - Venue shortlist ID
   * @param sessionId - OTP session ID (optional for authenticated platform users)
   */
  async getVenueBudgetBreakdown(
    token: string,
    shortlistId: string,
    sessionId?: string
  ): Promise<{
    roomBreakdown: any;
    activitiesBreakdown: any;
    mealsBreakdown: any;
    meetingsBreakdown: any;
    otherCosts: any;
  }> {
    const headers: Record<string, string> = {};
    if (sessionId) {
      headers['X-Session-Id'] = sessionId;
    }
    
    const res = await apiFetch(`/api/public/budget/venue/${shortlistId}?share_link_token=${encodeURIComponent(token)}`, {
      headers,
      skipRedirect: true,
      credentials: 'include',
    });
    const data = await parseResponse<{
      roomBreakdown: any;
      activitiesBreakdown: any;
      mealsBreakdown: any;
      meetingsBreakdown: any;
      otherCosts: any;
    }>(res, 'Failed to get venue budget breakdown');
    return data;
  }

  /**
   * Get full budget for event (public share link)
   * @param token - Share link token
   * @param eventId - Event ID
   * @param sessionId - OTP session ID (optional for authenticated platform users)
   */
  async getFullBudget(
    token: string,
    eventId: string,
    sessionId?: string
  ): Promise<any> {
    const headers: Record<string, string> = {};
    if (sessionId) {
      headers['X-Session-Id'] = sessionId;
    }
    
    const res = await apiFetch(`/api/public/budget/full/${eventId}?share_link_token=${encodeURIComponent(token)}`, {
      headers,
      skipRedirect: true,
      credentials: 'include',
    });
    const data = await parseResponse<any>(res, 'Failed to get full budget');
    return data;
  }

  /**
   * Get exchange rates (public, no auth needed)
   */
  async getExchangeRates(): Promise<any> {
    return { rates: await getBudgetExchangeRates() };
  }
}

// Export singleton instance
export const proposalService = new ProposalService();
